/**
 * @file Reads a `pipelineByStage` answer into the Pipeline by stage card's
 * model: every served row, one per form type and released flag, in served
 * order, each with an exact bar percent of the largest row from
 * `analyticsBarPercents`. Consumer: `AnalyticsPipelineByStageCard`. Rows are
 * never added up across types. While the value column is refused, bars
 * measure project counts instead, so a bar never reads as zero; a refused
 * released split (`refusedReleased`) or project count (`refusedProjects`) is
 * carried, its cells then null. A malformed row fails the whole model.
 */
import type { Snapshot } from 'valtio';

import { analyticsDecimalFraction } from '#types';
import type { NamedQueryCell } from '#types';

import { analyticsBarMeasure } from './analyticsBarMeasure';
import { analyticsBarPercents } from './analyticsBarPercents';
import { analyticsMalformedAnswer } from './analyticsMalformedAnswer';
import { analyticsRefusedColumn } from './analyticsRefusedColumn';
import { analyticsUnansweredFigure } from './analyticsUnansweredFigure';
import type {
  AnalyticsPipeline,
  AnalyticsPipelineRow,
} from './analyticsProjectsTab.types';
import type { AnalyticsAnswer } from './index.types';

type PipelineRow = Omit<AnalyticsPipelineRow, 'barPercent'>;

const pipelineRow = (
  row: Snapshot<Record<string, NamedQueryCell>>,
  projectsRefused: boolean,
): PipelineRow | undefined => {
  const { stage, released, projects, value } = row;
  const valid =
    typeof stage === 'string' &&
    (typeof released === 'boolean' || released === null) &&
    ((typeof projects === 'number' && Number.isInteger(projects)) ||
      (projects === null && projectsRefused)) &&
    (value === null ||
      (typeof value === 'string' &&
        analyticsDecimalFraction(value) !== undefined));
  return valid ? { stage, released, projects, value } : undefined;
};

export const analyticsPipeline = (
  answer: Snapshot<AnalyticsAnswer> | undefined,
): AnalyticsPipeline => {
  if (answer?.status !== 'answered') return analyticsUnansweredFigure(answer);
  const { result } = answer;
  const refusedValue = analyticsRefusedColumn(result, 'value');
  const refusedReleased = analyticsRefusedColumn(result, 'released');
  const refusedProjects = analyticsRefusedColumn(result, 'projects');
  const read = result.rows.map((row) =>
    pipelineRow(row, refusedProjects !== null),
  );
  const rows = read.filter((row): row is PipelineRow => row !== undefined);
  if (rows.length !== read.length)
    return analyticsMalformedAnswer('pipelineByStage');
  const barPercents = analyticsBarPercents(
    rows.map(({ projects, value }) =>
      analyticsBarMeasure(value, projects ?? 0, refusedValue),
    ),
  );
  return {
    status: 'answered',
    rows: rows.map((row, index) => ({
      ...row,
      barPercent: barPercents[index],
    })),
    refusedValue,
    refusedReleased,
    refusedProjects,
  };
};
